/*
 * ThinSlim decorator for the Recharge bundles widget
 * (sections/thinslim-byo-intro.liquid, product thinslim-build-your-own-box).
 *
 * - Copies each box item's label rows (rendered by the theme as hidden
 *   templates keyed by product ID) into the matching Recharge product card.
 * - Keeps the desktop summary below the sticky header, so the frequency
 *   choice and the add button stay on screen.
 *
 * Recharge renders the widget with React after load and may re-render cards.
 * Only nodes the theme adds are touched; Recharge's own nodes are not edited.
 */
(() => {
  const WIDGET = '.recharge-bundles-widget';
  const ITEM = '.recharge-bundle-product-item[data-recharge-product-id]';
  const FIGS_CLASS = 'tsf-rc-figs';

  const figureTemplates = new Map();
  document.querySelectorAll('[data-tsf-rc-figures] template[data-product-id]').forEach((template) => {
    figureTemplates.set(template.dataset.productId, template);
  });

  function decorateItem(item) {
    if (item.querySelector(`.${FIGS_CLASS}`)) return;
    const template = figureTemplates.get(item.dataset.rechargeProductId);
    const titles = item.querySelector('.recharge-bundle-product-titles');
    if (!template || !titles || !template.content.childElementCount) return;
    const figs = document.createElement('div');
    figs.className = FIGS_CLASS;
    figs.appendChild(template.content.cloneNode(true));
    titles.insertAdjacentElement('afterend', figs);
    item.classList.add('tsf-rc-item--figs');
  }

  function stickyTop() {
    const header = document.querySelector('header-component, #header-component');
    if (!header) return 0;
    const sticky = header.getAttribute('sticky');
    if (!sticky || sticky === 'never') return 0;
    return Math.ceil(header.getBoundingClientRect().height);
  }

  function setTop(widget) {
    const top = stickyTop();
    const value = `${top}px`;
    if (widget.style.getPropertyValue('--tsf-rc-top') !== value) widget.style.setProperty('--tsf-rc-top', value);
  }

  // The desktop summary list scrolls inside the sticky card. Keyboard users
  // need to reach it even before it holds any focusable remove buttons.
  function labelScroller(widget) {
    widget.querySelectorAll('.recharge-bundle-summary-sidebar .recharge-bundle-selected-products').forEach((list) => {
      const scroller = list.parentElement;
      if (!scroller || scroller.hasAttribute('data-tsf-rc-scroller')) return;
      scroller.setAttribute('data-tsf-rc-scroller', '');
      scroller.setAttribute('tabindex', '0');
      scroller.setAttribute('role', 'region');
      scroller.setAttribute('aria-label', 'Packages in your box');
    });
  }

  // Recharge's own ARIA structure fails two WCAG checks that matter for a
  // screen reader: the product grid has gridcells with no row between them and
  // the grid, and the summary list's listitems sit two wrappers below the list.
  // Both are attribute-level fixes. Reported to Recharge; re-applied here on
  // every re-render until their markup changes.
  function setRole(el, role) {
    // Only write when the value differs: the observer below watches this subtree.
    if (el.getAttribute('role') !== role) el.setAttribute('role', role);
  }

  function repairAria(widget) {
    widget.querySelectorAll('[role="grid"]').forEach((grid) => {
      grid.querySelectorAll(':scope > [role="gridcell"]').forEach((cell) => setRole(cell, 'listitem'));
      setRole(grid, 'list');
    });
    widget.querySelectorAll('[role="list"] [role="listitem"]').forEach((item) => {
      let parent = item.parentElement;
      while (parent && !parent.getAttribute('role')) {
        parent.setAttribute('role', 'presentation');
        parent = parent.parentElement;
      }
    });
    // An empty summary list is a list with no list items. Drop the role until
    // the shopper picks something.
    widget.querySelectorAll('.recharge-bundle-selected-products').forEach((list) => {
      setRole(list, list.querySelector('[role="listitem"]') ? 'list' : 'presentation');
    });
  }

  function decorate(widget) {
    widget.querySelectorAll(ITEM).forEach(decorateItem);
    labelScroller(widget);
    repairAria(widget);
  }

  function init(widget) {
    if (widget.dataset.tsfRcReady) return;
    widget.dataset.tsfRcReady = 'true';
    widget.classList.add('tsf-rc');
    let scheduled = false;
    const run = () => {
      scheduled = false;
      decorate(widget);
    };
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(run);
    });
    observer.observe(widget, { childList: true, subtree: true });
    decorate(widget);
    setTop(widget);
    window.addEventListener('resize', () => setTop(widget), { passive: true });
  }

  function start() {
    document.querySelectorAll(WIDGET).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
